import type { FC } from "react";
import {
  Tool01,
  Calendar,
  Users01,
  SearchLg,
  Package,
  File06,
  Archive,
  Receipt,
} from "@untitledui/icons";

interface Feature {
  title: string;
  description: string;
  icon: FC<{ className?: string }>;
  color: string;
}

const features: Feature[] = [
  {
    title: "Job Management",
    description:
      "Create work orders, assign technicians, track status from pending to invoiced. Full diagnosis and notes per job.",
    icon: Tool01,
    color: "bg-brand-100 text-brand-600",
  },
  {
    title: "Appointment Scheduling",
    description:
      "Let customers book online or schedule internally. See your calendar at a glance with vehicle and service details.",
    icon: Calendar,
    color: "bg-success-100 text-success-600",
  },
  {
    title: "Customer Database",
    description:
      "Store customer profiles, contact info, and vehicle history. Know your regulars and their service records.",
    icon: Users01,
    color: "bg-purple-100 text-purple-600",
  },
  {
    title: "Vehicle Records",
    description:
      "Track make, model, year, VIN, mileage, and full service history per vehicle. Linked to customer profiles.",
    icon: SearchLg,
    color: "bg-warning-100 text-warning-600",
  },
  {
    title: "Parts Inventory",
    description:
      "Monitor stock levels, set minimum thresholds, track cost vs. sell price, and know exactly what's on the shelf.",
    icon: Package,
    color: "bg-orange-100 text-orange-600",
  },
  {
    title: "Service Catalog",
    description:
      "Define your service offerings with categories, base pricing, and estimated durations. Keep your menu consistent.",
    icon: File06,
    color: "bg-teal-100 text-teal-600",
  },
  {
    title: "Storage Rentals",
    description:
      "Manage indoor/outdoor units (S\u2013XL), climate-controlled options, monthly rates, and rental status at a glance.",
    icon: Archive,
    color: "bg-pink-100 text-pink-600",
  },
  {
    title: "Invoicing & Billing",
    description:
      "Auto-generate invoices from completed jobs or storage rentals. Line-item detail with parts, labor, and tax.",
    icon: Receipt,
    color: "bg-error-100 text-error-600",
  },
];

export default function Features() {
  return (
    <section id="features" className="section-padding bg-bg-secondary">
      <div className="landing-container">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="heading-2 text-text-primary mb-4">
            Everything You Need to{" "}
            <span className="text-gradient">Manage Your Shop</span>
          </h2>
          <p className="subtitle-2">
            From job tracking to invoicing, every tool a modern garage needs
            — built into one platform.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {features.map((feature, index) => (
            <div
              key={feature.title}
              className="bg-bg-primary rounded-xl p-6 card-shadow-hover border border-border-secondary animate-fade-in"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              <div
                className={`w-10 h-10 rounded-lg flex items-center justify-center mb-4 ${feature.color}`}
              >
                <feature.icon className="w-5 h-5" />
              </div>
              <h3 className="text-md font-semibold text-text-primary mb-2">
                {feature.title}
              </h3>
              <p className="text-sm text-text-tertiary leading-relaxed">
                {feature.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
